import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import Avatar from '@mui/material/Avatar'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ErrorIcon from '@mui/icons-material/Error'

import CarteVitaleImg from '@/assets/img/cartevitale.png'
import { PatientProfileFormData } from '@/features/patient/patient-profile-form'

import { getGenderFromNir } from '../lib/carteVitaleHelpers'
import { usePlatformVitaleCardDialog } from '../model/usePlatformVitaleCardDialog'

export type PlatformVitaleCardDialogProps = {
  onClose?: () => void
  onSelect?: (info: Partial<PatientProfileFormData>) => void
  open: boolean
}

export function PlatformVitaleCardDialog(props: PlatformVitaleCardDialogProps) {
  const { onClose, onSelect, open } = props
  const { t } = useTranslation()

  const { error, infos, loading } = usePlatformVitaleCardDialog({ open })

  const handleSelect = (info: Record<string, string>) => () => {
    onSelect?.({
      name: info.prenomUsuel,
      surname: info.nomUsuel,
      gender: getGenderFromNir(info.nir),
      secu: info.nir.replaceAll(' ', ''),
      caisse: info.caisse,
      codeGestion: info.codeGestion,
      codeRegime: info.codeRegime,
      birthday: dayjs(info.date, 'DDMMYYYY').toDate(),
    })
    onClose?.()
  }

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          minWidth: '400px',
        },
      }}
    >
      <DialogContent>
        {!!error && (
          <Stack alignItems="center" justifyContent="center" spacing={1}>
            <ErrorIcon color="error" />
            <Typography variant="body1" color="error">
              {t('page.createPatient.carteVitaleLectorError.message')}
            </Typography>
          </Stack>
        )}
        {loading ? (
          <List>
            <Typography variant="body1">{t('common:loading')}</Typography>
            <ListItem>
              <ListItemAvatar>
                <Avatar>
                  <Skeleton animation="wave" variant="circular" width={40} height={40} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={<Skeleton animation="wave" height={10} width="80%" sx={{ mb: '6px' }} />}
                secondary={<Skeleton animation="wave" height={10} width="40%" />}
              />
            </ListItem>
            <ListItem>
              <ListItemAvatar>
                <Avatar>
                  <Skeleton animation="wave" variant="circular" width={40} height={40} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={<Skeleton animation="wave" height={10} width="80%" sx={{ mb: '6px' }} />}
                secondary={<Skeleton animation="wave" height={10} width="40%" />}
              />
            </ListItem>
          </List>
        ) : (
          <List>
            {infos.map((info, index) => {
              return (
                <ListItem key={info.nir || index} disablePadding>
                  <ListItemButton onClick={handleSelect(info)}>
                    <ListItemAvatar sx={{ minWidth: '80px' }}>
                      <Avatar
                        alt="carte-vitale"
                        src={CarteVitaleImg}
                        variant="rounded"
                        sx={{
                          width: '60px',
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText id={info.nir} primary={info.nomUsuel} secondary={info.prenomUsuel} />
                  </ListItemButton>
                </ListItem>
              )
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  )
}
